import { useState, useEffect } from 'react'
import { FullScreenModal } from '../../components/ui/FullScreenModal.tsx'
import { kitCollectionsApi, fieldOptionsApi, type FieldOption, type KitCollection } from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'
import { IconPencil } from '@tabler/icons-react'

type OptionWithId = { value: string; label: string; id: string }

function toOptions(items: FieldOption[]): OptionWithId[] {
  return items.map(i => ({ value: i.value, label: i.displayLabel, id: i.id }))
}

const STATIC_VISIBILITIES: OptionWithId[] = [
  { value: 'private', label: 'Private', id: 'private' },
  { value: 'shared', label: 'Shared', id: 'shared' },
  { value: 'community', label: 'Community', id: 'community' },
  { value: 'public', label: 'Public', id: 'public' },
]

const STATIC_BAG_TYPES: OptionWithId[] = [
  { value: 'general', label: 'General', id: 'general' },
  { value: 'for_trade_sale', label: 'For trade/sale', id: 'for_trade_sale' },
]

function RadioBubbles({ options, value, onChange, readOnly }: {
  options: OptionWithId[]; value: string; onChange: (id: string) => void; readOnly?: boolean
}) {
  if (readOnly) {
    const sel = options.find(o => o.id === value)
    return <span className="text-sm text-zinc-300">{sel?.label ?? '—'}</span>
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt.id} type="button" onClick={() => onChange(value === opt.id ? '' : opt.id)}
          className={[
            'px-3 py-1.5 rounded-full text-sm border transition-all',
            value === opt.id
              ? 'bg-brand-orange border-brand-orange text-white font-medium'
              : 'border-zinc-500 text-zinc-200 hover:border-zinc-300 hover:text-white',
          ].join(' ')}>
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-zinc-200 mb-1.5">{label}</label>
      {children}
    </div>
  )
}

type Props = {
  open: boolean
  onClose: () => void
  onSaved?: () => void
  bag?: KitCollection | null
  mode?: 'new' | 'view' | 'edit'
}

export function KitCollectionForm({ open, onClose, onSaved, bag, mode: initialMode = 'new' }: Props) {
  const { success, error } = useToast()
  const [mode, setMode] = useState(initialMode)
  const isView = mode === 'view'
  const isNew = mode === 'new'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibilityId, setVisibilityId] = useState('')
  const [bagTypeId, setBagTypeId] = useState('')
  const [saving, setSaving] = useState(false)
  const [optsLoaded, setOptsLoaded] = useState(false)
  const [visibilities, setVisibilities] = useState<OptionWithId[]>(STATIC_VISIBILITIES)
  const [bagTypes, setBagTypes] = useState<OptionWithId[]>(STATIC_BAG_TYPES)

  useEffect(() => {
    if (!open) return
    setMode(initialMode)
    setOptsLoaded(false)

    Promise.all([
      fieldOptionsApi.getByAttribute('visibility').catch(() => ({ data: [] })),
      fieldOptionsApi.getByAttribute('collection-type').catch(() => ({ data: [] })),
    ]).then(([vis, types]) => {
      const v = toOptions(vis.data ?? [])
      const t = toOptions(types.data ?? [])
      const visOpts = v.length ? v : STATIC_VISIBILITIES
      const typeOpts = t.length ? t : STATIC_BAG_TYPES
      setVisibilities(visOpts)
      setBagTypes(typeOpts)

      if (bag) {
        // Edit/view — populate from bag
        setName(bag.name)
        setDescription(bag.description ?? '')
        setVisibilityId(bag.visibilityId ?? visOpts.find(x => x.value === 'private')?.id ?? '')
        setBagTypeId(bag.collectionTypeId ?? typeOpts.find(x => x.value === 'general')?.id ?? '')
      } else {
        // New — set defaults
        setName('')
        setDescription('')
        setVisibilityId(visOpts.find(x => x.value === 'private')?.id ?? visOpts[0]?.id ?? '')
        setBagTypeId(typeOpts.find(x => x.value === 'general')?.id ?? typeOpts[0]?.id ?? '')
      }
      setOptsLoaded(true)
    })
  }, [open, bag?.id, initialMode])

  const handleSave = async (keepOpen = false) => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        visibilityId: visibilityId || null,
        collectionTypeId: bagTypeId || null,
      }
      if (isNew) {
        await kitCollectionsApi.create(payload)
        success('Kit collection created', name)
      } else if (bag) {
        await kitCollectionsApi.update(bag.id, payload)
        success('Kit collection updated', name)
      }
      onSaved?.()
      if (!keepOpen) onClose()
    } catch (e: any) {
      error(isNew ? 'Failed to create kit collection' : 'Failed to update kit collection', e.message)
    } finally {
      setSaving(false)
    }
  }

  const modalTitle = isNew
    ? (name.trim() || 'New Kit Collection')
    : (name.trim() || bag?.name || 'Kit Collection')

  const modalSubtitle = isNew
    ? 'Create a kit collection to organise and share your gear'
    : isView ? 'Kit collection details' : 'Edit kit collection'

  const editButton = isView ? (
    <button onClick={() => setMode('edit')}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised transition-colors">
      <IconPencil size={13} /> Edit
    </button>
  ) : undefined

  const footer = !isView ? (
    <>
      <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">Cancel</button>
      <button onClick={() => handleSave(true)} disabled={saving || !name.trim() || !optsLoaded}
        className="px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
      <button onClick={() => handleSave(false)} disabled={saving || !name.trim() || !optsLoaded}
        className="px-5 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
        {saving ? 'Saving...' : 'Save & Close'}
      </button>
    </>
  ) : undefined

  return (
    <FullScreenModal open={open} onClose={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
      headerActions={editButton}
      footer={footer}
      narrow>
      <div className="px-5">
        <div className="py-5 border-b border-border-default">
          <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-400 mb-4">Details</h3>
          <Field label="Name">
            {isView
              ? <p className="text-sm text-zinc-300">{name || '—'}</p>
              : <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Hockey kit, Leather weekend bag..."
                  className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors" />}
          </Field>
          <Field label="Description">
            {isView
              ? <p className="text-sm text-zinc-400">{description || '—'}</p>
              : <textarea value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Optional description..." rows={3}
                  className="w-full px-3 py-2.5 bg-surface-raised border border-border-default rounded-lg text-sm text-zinc-200 placeholder-zinc-500 outline-none focus:border-brand-orange/60 transition-colors resize-none" />}
          </Field>
          <Field label="Type">
            <RadioBubbles options={bagTypes} value={bagTypeId} onChange={setBagTypeId} readOnly={isView} />
          </Field>
        </div>
        <div className="py-5">
          <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-400 mb-4">Visibility & Sharing</h3>
          <Field label="Visibility">
            <RadioBubbles options={visibilities} value={visibilityId} onChange={setVisibilityId} readOnly={isView} />
          </Field>
        </div>
        <div className="h-8" />
      </div>
    </FullScreenModal>
  )
}
